import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Subject, Subscription } from 'rxjs';
import { Status as DeviationStatus } from 'src/app/_models';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from '../../kuba.services';
import {
  formatCalendarDate,
  percentOf,
  reportFileName
} from './safety-round.logic';
import {
  AssignedSafetyRoundTemplate,
  SafetyRoundDetail,
  SafetyRoundItemDeviation,
  SafetyRoundGrade,
  SafetyRoundStatus,
  SafetyRoundSummary
} from './safety-round.models';
import { FlexiSafetyRoundService, saveBlob } from './safety-round.service';

/** The no-template radio. Template ids are positive, so 0 never collides. */
const NO_TEMPLATE = 0;

/** One finding chip on a summary row: grade and how many items got it. */
interface FindingChip {
  grade: SafetyRoundGrade;
  count: number;
}

/**
 * Flexi › Safety rounds: the business's inspections and finding follow-up.
 *
 * The latest in-progress round and the latest completed one get a card each;
 * every round is in the history grid. Authoring follows the backend permission
 * endpoint; completed-round corrections remain restricted to administrators.
 */
@Component({
  selector: 'flexi-safety-round-list',
  templateUrl: './safety-round-list.component.html',
  styleUrls: ['./safety-round-list.component.scss'],
  standalone: false
})
export class SafetyRoundListComponent implements OnInit, OnDestroy {
  readonly Status = SafetyRoundStatus;
  readonly noTemplate = NO_TEMPLATE;
  readonly percentOf = percentOf;

  readonly grades = [
    SafetyRoundGrade.Satisfactory,
    SafetyRoundGrade.ShouldImprove,
    SafetyRoundGrade.LegalBreach,
    SafetyRoundGrade.DangerToLifeOrHealth,
    SafetyRoundGrade.NotApplicable
  ];
  readonly findingGrades = [
    SafetyRoundGrade.ShouldImprove,
    SafetyRoundGrade.LegalBreach,
    SafetyRoundGrade.DangerToLifeOrHealth
  ];
  completedRounds: SafetyRoundSummary[] = [];
  maximumFindings = 1;
  readonly overviewPageSize = 5;
  overviewPage = 1;
  overviewSearch = '';
  overviewTemplate = '';
  overviewFrom = '';
  overviewTo = '';
  filteredRounds: SafetyRoundSummary[] = [];
  visibleRounds: SafetyRoundSummary[] = [];

  get overviewTemplates(): string[] {
    return [
      ...new Set(
        this.completedRounds
          .map(round => round.TemplateName)
          .filter((name): name is string => !!name)
      )
    ].sort();
  }

  get invalidOverviewDates(): boolean {
    return (
      !!this.overviewFrom &&
      !!this.overviewTo &&
      this.overviewFrom > this.overviewTo
    );
  }

  get overviewRangeStart(): number {
    return this.filteredRounds.length
      ? (this.overviewPage - 1) * this.overviewPageSize + 1
      : 0;
  }

  get overviewRangeEnd(): number {
    return Math.min(
      this.overviewPage * this.overviewPageSize,
      this.filteredRounds.length
    );
  }

  private readonly businessCalendar = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Oslo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  selectedRound: SafetyRoundSummary | null = null;
  roundDetail: SafetyRoundDetail | null = null;
  detailLoading = false;
  detailFailed = false;
  private listRequest?: Subscription;
  private detailRequest?: Subscription;
  private permissionsRequest?: Subscription;
  private loadedBusinessId: number | null = null;
  get isAdmin(): boolean {
    return BaseServices.AbsoluteroleId === '1';
  }
  rounds: SafetyRoundSummary[] = [];
  loading = true;
  loadFailed = false;

  /** Latest in-progress round; the list is newest first. */
  inProgress: SafetyRoundSummary | null = null;
  lastCompleted: SafetyRoundSummary | null = null;

  /* ---- Start dialog ---- */
  startOpen = false;
  templates: AssignedSafetyRoundTemplate[] = [];
  templatesLoading = false;
  templatesFailed = false;
  selectedTemplateId: number | null = null;
  starting = false;

  /** Rounds with a request in flight (delete, report). */
  busyActions = new Map<number, string>();

  @ViewChild('rowMenu') rowMenu?: Menu;
  rowMenuItems: MenuItem[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiSafetyRoundService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private confirmation: ConfirmationService,
    private notifications: NotificationService
  ) {}

  get businessId(): number {
    const businessRoute = this.route.pathFromRoot.find(
      route => route.routeConfig?.path === 'business/:id'
    );
    return (
      Number(businessRoute?.snapshot.paramMap.get('id')) ||
      BaseServices.BusinessId
    );
  }

  /**
   * Admin or a listed HSE (BHT) advisor of the business — as
   * answered by the backend's permissions endpoint. False until it answers, so
   * nobody briefly sees controls the backend would refuse.
   */
  canAuthor = false;

  ngOnInit(): void {
    this.loadBusiness();
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (
        event instanceof NavigationEnd &&
        this.loadedBusinessId !== this.businessId
      ) {
        this.loadBusiness();
      }
    });
  }

  private loadBusiness(): void {
    const businessId = this.businessId;
    this.selectedRound = null;
    this.overviewSearch = '';
    this.overviewTemplate = '';
    this.overviewFrom = '';
    this.overviewTo = '';
    this.overviewPage = 1;
    this.loadedBusinessId = businessId;
    this.permissionsRequest?.unsubscribe();
    this.canAuthor = false;
    this.startOpen = false;
    this.templates = [];
    this.permissionsRequest = this.api
      .permissions(businessId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(permissions => {
        if (businessId === this.businessId)
          this.canAuthor = permissions.CanAuthor;
      });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.listRequest?.unsubscribe();
    this.detailRequest?.unsubscribe();
    const businessId = this.businessId;
    const selectedId =
      this.loadedBusinessId === businessId ? this.selectedRound?.Id : null;
    this.rounds = [];
    this.completedRounds = [];
    this.filteredRounds = [];
    this.visibleRounds = [];
    this.maximumFindings = 1;
    this.selectedRound = null;
    this.roundDetail = null;
    this.inProgress = null;
    this.lastCompleted = null;
    this.detailLoading = false;
    this.detailFailed = false;
    this.loading = true;
    this.loadFailed = false;
    this.listRequest = this.api
      .list(businessId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: rows => {
          if (businessId !== this.businessId) return;
          this.rounds = rows;
          this.completedRounds = rows
            .filter(r => r.Status === SafetyRoundStatus.Completed)
            .sort(
              (a, b) => a.RoundDate.localeCompare(b.RoundDate) || a.Id - b.Id
            );
          this.inProgress =
            rows.find(r => r.Status === SafetyRoundStatus.InProgress) ?? null;
          this.lastCompleted =
            this.completedRounds[this.completedRounds.length - 1] ?? null;
          this.applyOverviewFilters(false, selectedId);
        },
        error: () => {
          if (businessId === this.businessId) this.loadFailed = true;
        }
      });
  }

  /** Filter the business summaries locally; load detail only for the visible selection. */
  applyOverviewFilters(
    resetPage = true,
    selectedId = this.selectedRound?.Id
  ): void {
    if (this.invalidOverviewDates) return;
    const search = this.overviewSearch.trim().toLocaleLowerCase();
    this.filteredRounds = this.completedRounds
      .filter(round => {
        const date = round.RoundDate.slice(0, 10);
        return (
          (!search ||
            `${round.Title} ${round.Location ?? ''}`
              .toLocaleLowerCase()
              .includes(search)) &&
          (!this.overviewTemplate ||
            round.TemplateName === this.overviewTemplate) &&
          (!this.overviewFrom || date >= this.overviewFrom) &&
          (!this.overviewTo || date <= this.overviewTo)
        );
      })
      .reverse();
    // Preserve one scale across every page of the same filtered result set.
    this.maximumFindings = this.filteredRounds.reduce(
      (max, round) => Math.max(max, this.findingTotal(round)),
      1
    );
    if (resetPage) this.overviewPage = 1;
    this.overviewPage = Math.min(
      this.overviewPage,
      Math.max(1, Math.ceil(this.filteredRounds.length / this.overviewPageSize))
    );
    this.showOverviewPage(selectedId);
  }

  resetOverviewFilters(): void {
    this.overviewSearch = '';
    this.overviewTemplate = '';
    this.overviewFrom = '';
    this.overviewTo = '';
    this.applyOverviewFilters();
  }

  changeOverviewPage(page: number): void {
    const pages = Math.ceil(this.filteredRounds.length / this.overviewPageSize);
    if (page < 1 || page > pages) return;
    this.overviewPage = page;
    this.showOverviewPage();
  }

  private showOverviewPage(selectedId = this.selectedRound?.Id): void {
    this.visibleRounds = this.filteredRounds.slice(
      (this.overviewPage - 1) * this.overviewPageSize,
      this.overviewPage * this.overviewPageSize
    );
    const selected =
      this.visibleRounds.find(round => round.Id === selectedId) ??
      this.visibleRounds[0];
    if (selected) {
      if (this.roundDetail?.Id !== selected.Id) this.selectRound(selected);
    } else {
      this.detailRequest?.unsubscribe();
      this.selectedRound = null;
      this.roundDetail = null;
      this.detailLoading = false;
      this.detailFailed = false;
    }
  }

  selectRound(round: SafetyRoundSummary): void {
    if (!this.visibleRounds.some(r => r.Id === round.Id)) return;
    this.detailRequest?.unsubscribe();
    const businessId = this.businessId;
    this.selectedRound = round;
    this.roundDetail = null;
    this.detailLoading = true;
    this.detailFailed = false;
    this.detailRequest = this.api
      .get(businessId, round.Id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.detailLoading = false))
      )
      .subscribe({
        next: detail => {
          if (
            businessId !== this.businessId ||
            this.selectedRound?.Id !== round.Id
          )
            return;
          if (
            detail.Id !== round.Id ||
            detail.Status !== SafetyRoundStatus.Completed
          ) {
            this.detailFailed = true;
            return;
          }
          this.roundDetail = detail;
        },
        error: () => {
          if (
            businessId === this.businessId &&
            this.selectedRound?.Id === round.Id
          )
            this.detailFailed = true;
        }
      });
  }

  /** Keep fractional widths so rare findings remain visible in larger inspections. */
  chartPercent(part: number, total: number): number {
    return total > 0 ? Math.min(100, (part * 100) / total) : 0;
  }

  findingCount(round: SafetyRoundSummary, grade: SafetyRoundGrade): number {
    return grade === SafetyRoundGrade.ShouldImprove
      ? round.ShouldImproveCount
      : grade === SafetyRoundGrade.LegalBreach
        ? round.LegalBreachCount
        : round.DangerCount;
  }

  gradeCount(grade: SafetyRoundGrade | null): number {
    return (
      this.roundDetail?.Items.filter(item => item.Grade === grade).length ?? 0
    );
  }

  get assessedCount(): number {
    return (
      this.roundDetail?.Items.filter(item => item.Grade !== null).length ?? 0
    );
  }

  get linkedDeviations(): SafetyRoundItemDeviation[] {
    return [
      ...new Map(
        (this.roundDetail?.Items ?? []).flatMap(item =>
          item.Deviation ? [[item.Deviation.Id, item.Deviation] as const] : []
        )
      ).values()
    ];
  }

  needsFollowup(deviation: SafetyRoundItemDeviation): boolean {
    return (
      deviation.Status !== DeviationStatus.Done &&
      deviation.Status !== DeviationStatus.Closed &&
      deviation.Status !== DeviationStatus.Rejected
    );
  }

  get followupCounts(): number[] {
    const deviations = this.linkedDeviations;
    return [
      deviations.filter(d => this.needsFollowup(d)).length,
      deviations.filter(
        d =>
          d.Status === DeviationStatus.Done ||
          d.Status === DeviationStatus.Closed
      ).length,
      deviations.filter(d => d.Status === DeviationStatus.Rejected).length
    ];
  }

  isOverdue(deviation: SafetyRoundItemDeviation): boolean {
    const today = this.businessCalendar.format(new Date());
    return (
      this.needsFollowup(deviation) &&
      !!deviation.Deadline &&
      deviation.Deadline.slice(0, 10) < today
    );
  }

  get overdueCount(): number {
    return this.linkedDeviations.filter(d => this.isOverdue(d)).length;
  }

  deviationTitle(id: number): string {
    return (
      this.roundDetail?.Items.find(item => item.Deviation?.Id === id)?.Title ??
      ''
    );
  }

  deviationStatusKey(status: number | null): string {
    switch (status) {
      case DeviationStatus.New:
        return 'FLEXI_SAFETY_ROUNDS.DEVIATIONS.NEW';
      case DeviationStatus.Open:
        return 'FLEXI_SAFETY_ROUNDS.DEVIATIONS.OPEN';
      case DeviationStatus.Done:
        return 'FLEXI_SAFETY_ROUNDS.DEVIATIONS.DONE';
      case DeviationStatus.Closed:
        return 'FLEXI_SAFETY_ROUNDS.OVERVIEW.CLOSED';
      case DeviationStatus.Rejected:
        return 'FLEXI_SAFETY_ROUNDS.DEVIATIONS.REJECTED';
      default:
        return 'FLEXI_SAFETY_ROUNDS.OVERVIEW.UNKNOWN_STATUS';
    }
  }

  deviationUrl(id: number): string {
    return `${this.router.url.split('/Flexi')[0]}/follow-up/deviations/edit/${id}`;
  }

  /** Finding chips, most severe first; grades with no items are left out. */
  findingChips(round: SafetyRoundSummary): FindingChip[] {
    return [
      {
        grade: SafetyRoundGrade.DangerToLifeOrHealth,
        count: round.DangerCount
      },
      { grade: SafetyRoundGrade.LegalBreach, count: round.LegalBreachCount },
      { grade: SafetyRoundGrade.ShouldImprove, count: round.ShouldImproveCount }
    ].filter(chip => chip.count > 0);
  }

  findingTotal(round: SafetyRoundSummary): number {
    return (
      round.ShouldImproveCount + round.LegalBreachCount + round.DangerCount
    );
  }

  open(round: SafetyRoundSummary): void {
    this.router.navigate([round.Id], { relativeTo: this.route });
  }

  /**
   * The business's deviation list. Built from the current URL's business
   * prefix (`/app/business/{id}`) rather than hardcoded, so it also holds in
   * R-mode windows.
   */
  get deviationsUrl(): string {
    return `${this.router.url.split('/Flexi')[0]}/Flexi/safety-rounds/deviations`;
  }

  /* ---------------- Row menu ---------------- */

  openRowMenu(event: Event, round: SafetyRoundSummary): void {
    event.stopPropagation();
    if (this.busyActions.has(round.Id)) return;
    this.rowMenuItems = this.buildRowMenu(round);
    this.rowMenu?.toggle(event);
  }

  /** Open first, the report next, the destructive action last and separated. */
  private buildRowMenu(round: SafetyRoundSummary): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.translate.instant('FLEXI_SAFETY_ROUNDS.LIST.OPEN'),
        icon: 'pi pi-arrow-right',
        command: () => this.open(round)
      }
    ];
    if (round.Status === SafetyRoundStatus.Completed) {
      items.push({
        label: this.translate.instant('FLEXI_SAFETY_ROUNDS.DOWNLOAD_REPORT'),
        icon: 'pi pi-file-pdf',
        command: () => this.downloadReport(round)
      });
    }
    if (this.isAdmin && round.Status === SafetyRoundStatus.Completed) {
      items.push({
        label: this.translate.instant('FLEXI_SAFETY_ROUNDS.LIST.REOPEN'),
        icon: 'pi pi-pencil',
        command: () => this.confirmReopen(round)
      });
    }
    if (
      this.isAdmin ||
      (this.canAuthor && round.Status === SafetyRoundStatus.InProgress)
    ) {
      items.push(
        { separator: true },
        {
          label: this.translate.instant('FLEXI_SAFETY_ROUNDS.LIST.DELETE'),
          icon: 'pi pi-trash',
          command: () => this.confirmDelete(round)
        }
      );
    }
    return items;
  }

  private confirmReopen(round: SafetyRoundSummary): void {
    if (!this.isAdmin || this.busyActions.has(round.Id)) {
      return;
    }
    this.confirmation.confirm({
      key: 'fvrList',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant(
        'FLEXI_SAFETY_ROUNDS.LIST.CONFIRM_REOPEN',
        { title: round.Title }
      ),
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        if (this.busyActions.has(round.Id)) return;
        this.busyActions.set(round.Id, 'FLEXI_SAFETY_ROUNDS.FEEDBACK.REOPENING');
        this.api
          .reopen(this.businessId, round.Id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.busyActions.delete(round.Id))
          )
          .subscribe({
            next: () => this.open(round),
            error: () => this.load()
          });
      }
    });
  }

  private confirmDelete(round: SafetyRoundSummary): void {
    this.confirmation.confirm({
      key: 'fvrList',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant(
        round.Status === SafetyRoundStatus.Completed
          ? 'FLEXI_SAFETY_ROUNDS.LIST.CONFIRM_DELETE_COMPLETED'
          : 'FLEXI_SAFETY_ROUNDS.LIST.CONFIRM_DELETE',
        { title: round.Title }
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        if (this.busyActions.has(round.Id)) return;
        this.busyActions.set(round.Id, 'FLEXI_SAFETY_ROUNDS.FEEDBACK.DELETING');
        this.api
          .delete(this.businessId, round.Id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.busyActions.delete(round.Id))
          )
          .subscribe({
            next: () => {
              this.notifications.success('UI_NOTIFICATIONS.DELETED');
              this.load();
            },
            // The interceptor already toasts the failure; a 409 means the round
            // was completed meanwhile, so refresh to show that.
            error: () => this.load()
          });
      }
    });
  }

  downloadReport(round: SafetyRoundSummary): void {
    if (this.busyActions.has(round.Id)) {
      return;
    }
    this.busyActions.set(round.Id, 'FLEXI_SAFETY_ROUNDS.FEEDBACK.REPORT');
    this.api
      .downloadReport(this.businessId, round.Id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.busyActions.delete(round.Id))
      )
      .subscribe({
        next: file => saveBlob(file.blob, file.fileName ?? this.reportFileName(round)),
        // The shared HTTP interceptor reports the failure; finalize enables retry.
        error: () => undefined
      });
  }

  private reportFileName(round: SafetyRoundSummary): string {
    return reportFileName(
      this.translate.instant('FLEXI_SAFETY_ROUNDS.REPORT_FILE_NAME'),
      round.RoundDate
    );
  }

  /* ---------------- Start dialog ---------------- */

  openStart(): void {
    if (!this.canAuthor) {
      return;
    }
    this.startOpen = true;
    this.templatesLoading = true;
    this.templatesFailed = false;
    this.templates = [];
    this.selectedTemplateId = null;
    this.api
      .templates(this.businessId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.templatesLoading = false))
      )
      .subscribe({
        next: templates => {
          this.templates = templates;
          const preferred = templates.find(t => t.IsDefault) ?? templates[0];
          this.selectedTemplateId = preferred ? preferred.Id : NO_TEMPLATE;
        },
        error: () => {
          this.templatesFailed = true;
          this.selectedTemplateId = NO_TEMPLATE;
        }
      });
  }

  closeStart(): void {
    if (!this.starting) {
      this.startOpen = false;
    }
  }

  continueInProgress(): void {
    const round = this.inProgress;
    this.startOpen = false;
    if (round) {
      this.open(round);
    }
  }

  start(): void {
    if (this.starting || this.selectedTemplateId === null) {
      return;
    }
    this.starting = true;
    const templateId =
      this.selectedTemplateId === NO_TEMPLATE ? null : this.selectedTemplateId;
    this.api
      .start(this.businessId, { TemplateId: templateId, Title: null })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.starting = false))
      )
      .subscribe(id => {
        this.startOpen = false;
        // Step 1 (Details) is where a fresh round opens.
        this.router.navigate([id], {
          relativeTo: this.route,
          queryParams: { step: 'details' }
        });
      });
  }

  /** Formats checkpoint and area counts with the last-used date. */
  templateMeta(template: AssignedSafetyRoundTemplate): string {
    const content = this.translate.instant(
      'FLEXI_SAFETY_ROUNDS.START.TEMPLATE_CONTENT',
      {
        items: template.ItemCount,
        sections: template.SectionCount
      }
    );
    const used = template.LastUsedOn
      ? this.translate.instant('FLEXI_SAFETY_ROUNDS.START.LAST_USED', {
          date: formatCalendarDate(template.LastUsedOn)
        })
      : this.translate.instant('FLEXI_SAFETY_ROUNDS.START.NEVER_USED');
    return `${content} · ${used}`;
  }
}
